import { useEffect, useSyncExternalStore } from "react";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { applyPalette, clearPalette } from "../lib/palettes";
import type { FamilyTheme } from "@family/shared";

const STORAGE_KEY = "theme-preference";

/** Subscribe to theme changes from any source (toggle in header, system pref). */
function subscribeToTheme(callback: () => void) {
  // Listen for storage changes (same tab won't fire, but covers multi-tab)
  window.addEventListener("storage", callback);
  // Observe data-theme attribute mutations (covers same-tab toggle)
  const observer = new MutationObserver(callback);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
  return () => {
    window.removeEventListener("storage", callback);
    observer.disconnect();
  };
}

function getMode(): "light" | "dark" {
  // Read from the DOM attribute (set by useTheme in the header)
  const attr = document.documentElement.getAttribute("data-theme");
  if (attr === "dark") return "dark";
  if (attr === "light") return "light";
  // Fallback: check localStorage then system
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "dark") return "dark";
  if (stored === "light") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Reads the family's theme from the membership and applies the corresponding
 * CSS palette to the document root. Re-applies when the theme (light/dark)
 * or the family palette changes.
 */
export function PaletteProvider() {
  const meQuery = useMe();
  const mode = useSyncExternalStore(subscribeToTheme, getMode);
  const membership = resolveMembership(meQuery.data);
  const familyTheme: FamilyTheme = (membership?.theme as FamilyTheme) ?? "classic";

  useEffect(() => {
    if (familyTheme === "classic") {
      clearPalette();
    } else {
      applyPalette(familyTheme, mode);
    }
    return () => clearPalette();
  }, [familyTheme, mode]);

  return null;
}
